import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { httpInterceptor } from '../../../API/interceptor';
import { getNeighborhoods } from '../../../API/apiService';
import CustomFilterSelect from '../../../shared/customFilterSelect';
import '../createSchool/style.scss';

const SCHOOL_TEXT_MAX_LENGTH = 255;
const ADVISORY_TYPE_MAX_LENGTH = 21;
const OTHER_ADVISORY_TYPE_ID = 'OTRO';

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getOptionName(options, optionId) {
  return (
    options.find(option => String(option?.id) === String(optionId))?.name || ''
  );
}

function isKnownOption(options, optionId) {
  return options.some(option => String(option?.id) === String(optionId));
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function CreateAdvisory({
  setOpenModal,
  schoolType = [{ name: 'Asesoría', id: 'ASESORIA' }],
  municipalities,
  users,
  created,
  mode = 'create',
  initialSchool = null,
  entityLabel = 'Asesoría',
  organizationFieldLabel = 'Nombre de la dependencia',
  allowedMediatorRoles = ['Mediador COP'],
  currentMediatorId = '',
  currentMediatorName = ''
}) {
  const isEditMode = mode === 'edit';
  const normalizedEntityLabel = String(entityLabel || 'Asesoría').trim();
  const normalizedOrganizationFieldLabel = String(
    organizationFieldLabel || 'Nombre de la dependencia'
  ).trim();
  const normalizedCurrentMediatorId = String(currentMediatorId || '').trim();
  const normalizedCurrentMediatorName = String(currentMediatorName || '').trim();
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [formData, setFormData] = useState({
    schoolId: '',
    code: '',
    name: '',
    municipality: '',
    neighborhood: '',
    type: '',
    otherAdvisoryType: '',
    year: '',
    resourceType: '',
    organization: '',
    additionalMediators: '',
    facilitator: ''
  });

  const toPositiveInt = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };

  const mediatorUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const normalizedAllowedRoles = Array.isArray(allowedMediatorRoles)
      ? allowedMediatorRoles
          .map(role => normalizeText(role))
          .filter(Boolean)
      : [];

    const unique = new Map();
    users.forEach(user => {
      if (!user?.id) return;

      const roleName = normalizeText(user.roleName);
      const isMediator =
        normalizedAllowedRoles.length > 0
          ? normalizedAllowedRoles.includes(roleName)
          : roleName.includes('mediad');
      const isActive = user.active === undefined ? true : Boolean(user.active);
      const normalizedName = String(user.name || '').trim();

      if (!isMediator || !isActive || !normalizedName) return;

      unique.set(String(user.id), {
        ...user,
        id: String(user.id),
        name: normalizedName
      });
    });

    return Array.from(unique.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'es', {
        sensitivity: 'base'
      })
    );
  }, [allowedMediatorRoles, users]);

  const currentSchoolId = String(initialSchool?.id ?? 'new');
  const initialMunicipalityId = toPositiveInt(
    initialSchool?.municipalityId ?? initialSchool?.municipality_id
  );
  const initialNeighborhoodId = toPositiveInt(
    initialSchool?.neighborhoodId ?? initialSchool?.neighborhood_id
  );
  const selectedTypeLabel = getOptionName(schoolType, formData.type);
  const isOtherAdvisoryType =
    String(formData.type || '').trim().toUpperCase() === OTHER_ADVISORY_TYPE_ID;
  const selectedMunicipalityLabel =
    getOptionName(municipalities, formData.municipality) ||
    (String(formData.municipality) === String(initialMunicipalityId)
      ? String(initialSchool?.municipality?.name || '').trim()
      : '');
  const selectedNeighborhoodLabel =
    getOptionName(neighborhoods, formData.neighborhood) ||
    (String(formData.neighborhood) === String(initialNeighborhoodId)
      ? String(initialSchool?.neighborhood?.name || '').trim()
      : '');
  const selectedMediatorLabel = formData.facilitator
    ? getOptionName(mediatorUsers, formData.facilitator) ||
      getUserDisplayName(initialSchool?.user) ||
      normalizedCurrentMediatorName
    : '';

  useEffect(() => {
    setFormData(prev => {
      if (!prev.facilitator) return prev;
      if (String(prev.facilitator) === normalizedCurrentMediatorId) return prev;

      const facilitatorExists = mediatorUsers.some(
        user => String(user.id) === String(prev.facilitator)
      );

      if (facilitatorExists) return prev;
      return { ...prev, facilitator: '' };
    });
  }, [mediatorUsers, normalizedCurrentMediatorId]);

  useEffect(() => {
    if (isEditMode && initialSchool) {
      const municipalityId = toPositiveInt(
        initialSchool?.municipalityId ?? initialSchool?.municipality_id
      );
      const neighborhoodId = toPositiveInt(
        initialSchool?.neighborhoodId ?? initialSchool?.neighborhood_id
      );
      const rawInitialType = String(
        initialSchool?.type ?? schoolType?.[0]?.id ?? 'ASESORIA'
      ).trim();
      const initialTypeIsKnown = isKnownOption(schoolType, rawInitialType);
      const facilitatorId = toPositiveInt(
        initialSchool?.userId ?? initialSchool?.user_id ?? initialSchool?.user?.id
      );

      setSelectedMunicipality(municipalityId);
      setFormData({
        schoolId: initialSchool?.id ?? '',
        code: initialSchool?.code ?? '',
        name: initialSchool?.name ?? initialSchool?.address ?? '',
        municipality: municipalityId || '',
        neighborhood: neighborhoodId || '',
        type: initialTypeIsKnown ? rawInitialType : OTHER_ADVISORY_TYPE_ID,
        otherAdvisoryType: initialTypeIsKnown ? '' : rawInitialType,
        year: initialSchool?.year ?? '',
        resourceType:
          initialSchool?.resourceTypeId ?? initialSchool?.resource_type_id ?? 'RO',
        organization: initialSchool?.organization ?? '',
        additionalMediators:
          initialSchool?.additionalMediators ??
          initialSchool?.additional_mediators ??
          '',
        facilitator: facilitatorId ? String(facilitatorId) : ''
      });
      return;
    }

    setSelectedMunicipality(null);
    setNeighborhoods([]);
    setFormData({
      schoolId: '',
      code: '',
      name: '',
      municipality: '',
      neighborhood: '',
      type: schoolType?.[0]?.id ?? 'ASESORIA',
      otherAdvisoryType: '',
      year: '',
      resourceType: '',
      organization: '',
      additionalMediators: '',
      facilitator: ''
    });
  }, [isEditMode, initialSchool, schoolType, normalizedCurrentMediatorId]);

  useEffect(() => {
    if (!selectedMunicipality) {
      setNeighborhoods([]);
      return;
    }

    let ignore = false;

    async function loadNeighborhoods() {
      const response = await getNeighborhoods(selectedMunicipality);
      if (!ignore && response) {
        setNeighborhoods(response);
      }
    }

    loadNeighborhoods();

    return () => {
      ignore = true;
    };
  }, [selectedMunicipality]);

  function handleChange(event) {
    event.preventDefault();
    let { name, value } = event.target;

    if (name === 'name') {
      value = String(value || '').toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function onSelectMunicipality(selectName, id) {
    const municipalityId = toPositiveInt(id);
    setSelectedMunicipality(municipalityId);
    setNeighborhoods([]);
    setFormData(prev => ({
      ...prev,
      municipality: municipalityId || '',
      neighborhood: ''
    }));
  }

  function onSelectNeighborhood(selectName, id) {
    const neighborhoodId = toPositiveInt(id);
    setFormData(prev => ({ ...prev, neighborhood: neighborhoodId || '' }));
  }

  function onSelectType(selectName, id) {
    setFormData(prev => ({
      ...prev,
      type: id || '',
      otherAdvisoryType:
        String(id || '').trim().toUpperCase() === OTHER_ADVISORY_TYPE_ID
          ? prev.otherAdvisoryType
          : ''
    }));
  }

  function onSelectMediator(selectName, id) {
    setFormData(prev => ({ ...prev, facilitator: id || '' }));
  }

  async function submitAdvisory() {
    const municipalityId = toPositiveInt(formData.municipality);
    const neighborhoodId = toPositiveInt(formData.neighborhood);

    if (!municipalityId || !neighborhoodId) {
      toast('Por favor seleccione una Comuna y un Barrio válidos', {
        type: 'warning'
      });
      return;
    }

    if (!formData.name) {
      toast(`Por favor digite el nombre de la ${normalizedEntityLabel}`, {
        type: 'warning'
      });
      return;
    }

    const advisoryType = String(formData.type || '').trim();
    const otherAdvisoryType = String(formData.otherAdvisoryType || '').trim();
    const typeToSave = isOtherAdvisoryType ? otherAdvisoryType : advisoryType;

    if (!advisoryType) {
      toast('Por favor seleccione el Tipo de Asesoría', {
        type: 'warning'
      });
      return;
    }

    if (isOtherAdvisoryType && !otherAdvisoryType) {
      toast('Por favor digite el otro tipo de asesoría', {
        type: 'warning'
      });
      return;
    }

    if (typeToSave.length > ADVISORY_TYPE_MAX_LENGTH) {
      toast(`El tipo de asesoría no puede superar ${ADVISORY_TYPE_MAX_LENGTH} caracteres`, {
        type: 'warning'
      });
      return;
    }

    if (!formData.facilitator) {
      toast('Por favor seleccione un mediador', {
        type: 'warning'
      });
      return;
    }

    const facilitatorValid = mediatorUsers.some(
      user => String(user.id) === String(formData.facilitator)
    ) || String(formData.facilitator) === normalizedCurrentMediatorId;

    if (!facilitatorValid) {
      toast('Por favor seleccione un mediador válido', {
        type: 'warning'
      });
      return;
    }

    const payload = {
      schoolId: toPositiveInt(formData.schoolId),
      name: String(formData.name || '').trim(),
      municipality: municipalityId,
      neighborhood: neighborhoodId,
      type: typeToSave,
      isCopAdvisory: true,
      organization: String(formData.organization || '').trim(),
      additionalMediators: String(formData.additionalMediators || '').trim(),
      facilitator: Number(formData.facilitator),
      resourceType: formData.resourceType || 'RO',
      year: isEditMode
        ? formData.year
        : new Date().getFullYear().toString(),
      groups: [],
      active: isEditMode ? undefined : true
    };

    try {
      const response = isEditMode
        ? await httpInterceptor.put('schools', payload)
        : await httpInterceptor.post('schools', payload);

      if (response?.status === 200 || response?.success) {
        setOpenModal(false);
        created(true);
      }
    } catch (error) {
      if (!isEditMode && error?.response?.data?.message?.code === '23505') {
        toast(`La ${normalizedEntityLabel} ya existe`, { type: 'error' });
        return;
      }

      if (error?.response?.data?.message === 'ADDITIONAL_MEDIATORS_COLUMN_MISSING') {
        toast('Falta ejecutar la migracion de mediadores adicionales', {
          type: 'warning'
        });
        return;
      }

      toast(
        isEditMode
          ? `Error actualizando ${normalizedEntityLabel}`
          : `Error creando ${normalizedEntityLabel}`,
        { type: 'error' }
      );
    }
  }

  return (
    <section className='main-create-container'>
      <p className='title'>
        {isEditMode
          ? `EDITAR ${normalizedEntityLabel.toUpperCase()}`
          : `CREAR ${normalizedEntityLabel.toUpperCase()}`}
      </p>

      <section className='create-container'>
        {isEditMode ? (
          <label className='label code-field'>
            <input
              placeholder={`Codigo de ${normalizedEntityLabel}`}
              type='text'
              name='code'
              className='input-form'
              value={formData.code}
              readOnly
            />
          </label>
        ) : null}

        <label className='label'>
          <input
            placeholder={`Nombre de ${normalizedEntityLabel}`}
            type='text'
            name='name'
            className='input-form'
            maxLength={SCHOOL_TEXT_MAX_LENGTH}
            value={formData.name}
            onChange={handleChange}
          />
        </label>

        <CustomFilterSelect
          key={`type-${currentSchoolId}-${formData.type || 'none'}`}
          optionsList={schoolType}
          placeholder='Tipo de Asesoría'
          addInfo={onSelectType}
          valueEdit={selectedTypeLabel}
        />

        {isOtherAdvisoryType ? (
          <label className='label'>
            <input
              placeholder='Otro tipo de asesoría'
              type='text'
              name='otherAdvisoryType'
              className='input-form'
              maxLength={ADVISORY_TYPE_MAX_LENGTH}
              value={formData.otherAdvisoryType}
              onChange={handleChange}
            />
          </label>
        ) : null}

        <div className='mediador-container'>
          <CustomFilterSelect
            key={`facilitator-${currentSchoolId}-${formData.facilitator || 'none'}`}
            optionsList={mediatorUsers}
            placeholder='Nombre Mediador/es'
            addInfo={onSelectMediator}
            valueEdit={selectedMediatorLabel}
            searchable={true}
            searchPlaceholder='Buscar mediador COP'
          />
        </div>

        <label className='label'>
          <input
            placeholder='Mediador(es) adicional(es) (opcional)'
            type='text'
            name='additionalMediators'
            className='input-form'
            maxLength={SCHOOL_TEXT_MAX_LENGTH}
            value={formData.additionalMediators}
            onChange={handleChange}
          />
        </label>

        <div className='place-info-container'>
          <CustomFilterSelect
            key={`municipality-${currentSchoolId}-${formData.municipality || 'none'}`}
            placeholder={'Comuna'}
            optionsList={municipalities}
            addInfo={onSelectMunicipality}
            valueEdit={selectedMunicipalityLabel}
          />
          <CustomFilterSelect
            key={`neighborhood-${currentSchoolId}-${selectedMunicipality || 'none'}-${formData.neighborhood || 'none'}`}
            placeholder={'Barrio'}
            optionsList={neighborhoods}
            addInfo={onSelectNeighborhood}
            searchable={true}
            searchPlaceholder='Escribe para buscar barrio'
            disabled={!selectedMunicipality}
            valueEdit={selectedNeighborhoodLabel}
          />
        </div>

        <div className='motherOrg-container'>
          <label className='label'>
            <input
              placeholder={normalizedOrganizationFieldLabel}
              type='text'
              name='organization'
              className='input-form'
              maxLength={SCHOOL_TEXT_MAX_LENGTH}
              value={formData.organization}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className='buttons-container'>
          <button className='blue-site-button create' onClick={submitAdvisory} type='button'>
            {isEditMode ? 'GUARDAR CAMBIOS' : 'CREAR'}
          </button>

          <button
            className='button-white-border-blue'
            onClick={() => setOpenModal(false)}
            type='button'
          >
            CANCELAR
          </button>
        </div>
      </section>
    </section>
  );
}

export default CreateAdvisory;
